// TransferSuccess.js
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Alert,
  AlertTitle,
} from '@mui/material';
import { styled } from '@mui/system';
import {
  CheckCircle,
  ArrowRight,
  Calendar,
  Package,
  Phone,
  Home
} from 'lucide-react';

// Styled components
const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(10),
  maxWidth: '800px',
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  backgroundColor: '#ffffff',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const TransferSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [transferData, setTransferData] = useState(null);

  useEffect(() => {
    // Get data from navigation state or URL params
    if (location.state) {
      setTransferData(location.state);
    }
  }, [location]);

  return (
    <StyledContainer>
      <StyledPaper>
        <Box 
          sx={{ 
            width: 80, 
            height: 80, 
            borderRadius: '50%', 
            bgcolor: '#DCFCE7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}
        >
          <CheckCircle size={40} color="#059669" />
        </Box>
        
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Transfer Successful!
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
          Your CircledIn subscription has been successfully transferred to your new carrier.
          You will receive a confirmation email shortly with your order details.
        </Typography>
        
        <Box sx={{ my: 4 }}>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <AlertTitle>Next Steps</AlertTitle>
            <Typography variant="body2" paragraph>
              <strong>Your SIM card</strong> will be shipped to you within 5-7 business days.
            </Typography>
            <Typography variant="body2">
              <strong>Your service</strong> will be activated as soon as you receive and activate your SIM card.
            </Typography>
          </Alert>
          
          <Alert 
            icon={<Calendar size={20} />}
            severity="success"
            sx={{ textAlign: 'left' }}
          >
            <AlertTitle>Your CircledIn Subscription</AlertTitle>
            <Typography variant="body2">
              Your existing subscription has been transferred to your new carrier.
              <br />
              {transferData?.existingSubscription?.monthly_payment_date && (
                <strong>Next billing date: {new Date(transferData.existingSubscription.monthly_payment_date).toLocaleDateString()}</strong>
              )}
            </Typography>
          </Alert>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={() => navigate('/my-profile')}
          >
            My Profile
          </Button>
          
          <Button
            variant="contained"
            endIcon={<ArrowRight />}
            onClick={() => navigate('/all-circles')}
            sx={{ 
              bgcolor: '#FF4775',
              '&:hover': {
                bgcolor: '#E5385F'
              }
            }}
          >
            Explore More Circles
          </Button>
        </Box>
      </StyledPaper>
    </StyledContainer>
  );
};

export default TransferSuccess;